import React, { useMemo, useState, useEffect } from 'react';
import ReactDOMServer from 'react-dom/server';
import { FaGoogle, FaCheckCircle, FaQrcode } from 'react-icons/fa';
import { FiEye, FiClipboard, FiTrash2 } from 'react-icons/fi';
import { QRCodeSVG } from 'qrcode.react';
import api from '../../../../services/api';
import { useAuth } from '../../../../context/AuthContext';
import './TaskModals.css';

const dayOrder = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const dayLabel = {
  sun: 'Sunday', mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday'
};

const emptyAvailability = {
  generic: { sun: [], mon: [], tue: [], wed: [], thu: [], fri: [], sat: [] },
  specific: []
};

const resolvePublicOrigin = () => {
  const { protocol, hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return window.location.origin;
  }
  if (hostname === 'bloomnav.com' || hostname.endsWith('.bloomnav.com')) {
    return `${protocol}//bloomnav.com`;
  }
  return window.location.origin;
};

const CalendarAvailabilityModal = ({ isOpen, onClose, availability = emptyAvailability, onSave }) => {
  const { user, company } = useAuth();
  const [mode, setMode] = useState('generic'); // 'generic' | 'specific'
  const [draft, setDraft] = useState(availability || emptyAvailability);
  const [specificDate, setSpecificDate] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.GOOGLE_OAUTH_CLIENT_ID || process.env.GOOGLE_CLIENT_ID;
  const [googleCalToken, setGoogleCalToken] = useState(null);
  const [googleCalTokenExpiry, setGoogleCalTokenExpiry] = useState(0);
  const [isCheckingCalendar, setIsCheckingCalendar] = useState(false);
  const [isCalendarConnected, setIsCalendarConnected] = useState(false);
  const [calendarAccount, setCalendarAccount] = useState(null);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [isSavingAvailability, setIsSavingAvailability] = useState(false);
  const [publicSlug, setPublicSlug] = useState('');
  const [slugError, setSlugError] = useState('');
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const [isSlugAvailable, setIsSlugAvailable] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [toast, setToast] = useState('');
  const [googleAuthorizeUrl, setGoogleAuthorizeUrl] = useState('');
  const GOOGLE_GRANTED_KEY = 'googleCalGranted';
  // Apply to all is now an explicit action button rather than a persistent switch

  // Load any stored Google token to reflect "Connected"
  useEffect(() => {
    try {
      const storedExpiry = Number(localStorage.getItem('googleCalTokenExpiry') || '0');
      const storedToken = localStorage.getItem('googleCalToken') || null;
      if (storedToken && Date.now() < storedExpiry) {
        setGoogleCalToken(storedToken);
        setGoogleCalTokenExpiry(storedExpiry);
        setIsCalendarConnected(true);
      }
    } catch {
      // ignore
    }
  }, []);

  const persistToken = (token, expiryMs) => {
    try {
      localStorage.setItem('googleCalToken', token || '');
      localStorage.setItem('googleCalTokenExpiry', String(expiryMs || 0));
    } catch {
      // ignore
    }
  };

  const persistGranted = () => {
    try {
      localStorage.setItem(GOOGLE_GRANTED_KEY, '1');
    } catch {
      // ignore
    }
  };

  const ensureGsiLoaded = () =>
    new Promise((resolve) => {
      if (window.google && window.google.accounts && window.google.accounts.oauth2) return resolve();
      const id = 'gsi-client';
      if (document.getElementById(id)) {
        const check = () => (window.google && window.google.accounts && window.google.accounts.oauth2) ? resolve() : setTimeout(check, 50);
        check();
        return;
      }
      const s = document.createElement('script');
      s.id = id;
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      document.head.appendChild(s);
    });

  const refreshCalendarConnectionStatus = async () => {
    setIsCheckingCalendar(true);
    try {
      try {
        const { data } = await api.get('/api/integrations/google/status');
        if (data?.is_success) {
          setIsCalendarConnected(!!data.connected);
          setCalendarAccount(data.googleEmail || null);
          return;
        }
      } catch (_) {
        // fall back to local GIS token below
      }

      const storedExpiry = Number(localStorage.getItem('googleCalTokenExpiry') || '0');
      const storedToken = localStorage.getItem('googleCalToken') || null;
      const valid = !!(storedToken && Date.now() < storedExpiry);
      setIsCalendarConnected(valid);
      if (!valid) setCalendarAccount(null);
    } catch (_) {
      setIsCalendarConnected(false);
      setCalendarAccount(null);
    } finally {
      setIsCheckingCalendar(false);
    }
  };

  const prefetchGoogleAuthorizeUrl = async () => {
    try {
      const { data } = await api.get('/api/integrations/google/authorize-link', {
        params: { returnOrigin: window.location.origin }
      });
      if (data?.is_success && data.authorizeUrl) {
        setGoogleAuthorizeUrl(data.authorizeUrl);
      }
    } catch (_) {
      setGoogleAuthorizeUrl('');
    }
  };

  useEffect(() => {
    const onGoogleCalendarConnected = (event) => {
      if (event?.data?.type === 'google-calendar-connected') {
        refreshCalendarConnectionStatus();
      }
    };
    window.addEventListener('message', onGoogleCalendarConnected);
    return () => window.removeEventListener('message', onGoogleCalendarConnected);
  }, []);

  const requestGoogleCalendarToken = (silent = false) =>
    new Promise(async (resolve, reject) => {
      try {
        if (!GOOGLE_CLIENT_ID) return reject(new Error('Missing GOOGLE_CLIENT_ID'));
        await ensureGsiLoaded();
        const gis = window.google && window.google.accounts && window.google.accounts.oauth2;
        if (!gis) return reject(new Error('Google OAuth script not loaded'));
        const isTokenValid = googleCalToken && Date.now() < googleCalTokenExpiry;
        const tokenClient = gis.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'https://www.googleapis.com/auth/calendar.readonly',
          prompt: silent ? '' : (isTokenValid ? '' : 'consent'),
          callback: async (resp) => {
            if (resp && resp.access_token) {
              const expiresInSec = Number(resp.expires_in || 3600);
              setGoogleCalToken(resp.access_token);
              const expiry = Date.now() + Math.max(0, (expiresInSec - 60) * 1000);
              setGoogleCalTokenExpiry(expiry);
              setIsCalendarConnected(true);
              persistToken(resp.access_token, expiry);
              persistGranted();
              // Save to backend
              try {
                await api.post('/api/integrations/google/save-access-token', {
                  accessToken: resp.access_token,
                  expiresInSec
                });
              } catch (_) {}
              resolve(resp.access_token);
            } else {
              reject(new Error('Failed to obtain access token'));
            }
          }
        });
        tokenClient.requestAccessToken({ prompt: silent ? '' : undefined });
      } catch (err) {
        reject(err);
      }
    });

  useEffect(() => {
    if (isOpen) {
      setDraft(availability || emptyAvailability);
      setMode('generic');
      setSpecificDate('');
      setSelectedDay(null); // onload, no day selected
      // keep selected days default to Mon
      refreshCalendarConnectionStatus();
      prefetchGoogleAuthorizeUrl();
      // Load availability from backend if logged-in user is available
      if (user?.id) {
        (async () => {
          setIsLoadingAvailability(true);
          try {
            const { data } = await api.get(`/api/users/${user.id}/availability`);
            if (data?.is_success && data.availability?.schedule) {
              setDraft(data.availability.schedule || emptyAvailability);
              setMode('generic');
              setSpecificDate('');
              setSelectedDay(null);
              setPublicSlug(data.availability.publicSlug || '');
            }
          } catch (e) {
            // ignore; keep existing draft
          } finally {
            setIsLoadingAvailability(false);
          }
        })();
      }
      // Load full company details (same API as Account page) to populate print card
      if (company?.id) {
        (async () => {
          try {
            const resp = await api.get(`/api/companies/${company.id}`);
            if (resp?.data?.is_success) {
              setCompanyDetails(resp.data.company || null);
            } else {
              setCompanyDetails(null);
            }
          } catch (_) {
            setCompanyDetails(null);
          }
        })();
      }
    }
  }, [isOpen, availability, googleCalToken, googleCalTokenExpiry]);

  // Debounced check for slug availability (must be declared before any early returns)
  useEffect(() => {
    const slug = String(publicSlug || '').trim();
    if (!slug) {
      setIsSlugAvailable(null);
      setIsCheckingSlug(false);
      return;
    }
    // Inline validation to avoid dependency on later-declared helpers
    if (!/^[a-z0-9-]{3,64}$/.test(slug)) {
      setIsSlugAvailable(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setIsCheckingSlug(true);
      try {
        const { data } = await api.get('/api/availability/slug/check', { params: { slug } });
        if (!cancelled) {
          setIsSlugAvailable(!!data?.available);
        }
      } catch (_) {
        if (!cancelled) {
          setIsSlugAvailable(null);
        }
      } finally {
        if (!cancelled) {
          setIsCheckingSlug(false);
        }
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [publicSlug]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  if (!isOpen) return null;

  const handleToggleDay = (day) => {
    // Clicking the already selected day deselects it and clears its hours
    if (selectedDay === day) {
      setDayHourSet(day, new Set()); // remove all time for that day
      setSelectedDay(null);
      return;
    }
    setSelectedDay(day);
  };

  // Helpers to convert between slots and set of hours (0-23)
  const pad2 = (n) => n.toString().padStart(2, '0');
  const slotsToHourSet = (slots = []) => {
    const set = new Set();
    (slots || []).forEach(({ start, end }) => {
      const sh = parseInt(String(start || '00:00').split(':')[0], 10);
      const eh = parseInt(String(end || '00:00').split(':')[0], 10);
      if (Number.isFinite(sh) && Number.isFinite(eh)) {
        for (let h = Math.max(0, sh); h < Math.min(24, eh); h++) {
          set.add(h);
        }
      }
    });
    return set;
  };
  const hourSetToSlots = (hourSet) => {
    const hours = Array.from(hourSet).sort((a, b) => a - b);
    const slots = [];
    let i = 0;
    while (i < hours.length) {
      const start = hours[i];
      let end = start + 1;
      i++;
      while (i < hours.length && hours[i] === end) {
        end++;
        i++;
      }
      slots.push({ start: `${pad2(start)}:00`, end: `${pad2(Math.min(24, end))}:00` });
    }
    return slots;
  };
  const getDayHourSet = (day) => slotsToHourSet(draft.generic?.[day] || []);
  const setDayHourSet = (day, set) => {
    setDraft(prev => {
      const next = { ...prev, generic: { ...prev.generic } };
      next.generic[day] = hourSetToSlots(set);
      return next;
    });
  };
  const isHourSelectedForSelectedDay = (hour) => {
    if (!selectedDay) return false;
    return getDayHourSet(selectedDay).has(hour);
  };
  const applyHourSetToAllDays = (set) => {
    setDraft(prev => {
      const next = { ...prev, generic: { ...prev.generic } };
      dayOrder.forEach((d) => {
        next.generic[d] = hourSetToSlots(set);
      });
      return next;
    });
  };
  const handleApplyCurrentToAll = () => {
    if (!selectedDay) return;
    const current = getDayHourSet(selectedDay);
    applyHourSetToAllDays(current);
  };
  const toggleHour = (hour) => {
    if (!selectedDay) return;
    const set = getDayHourSet(selectedDay);
    if (set.has(hour)) set.delete(hour); else set.add(hour);
    setDayHourSet(selectedDay, set);
  };

  const updateGenericSlot = (day, idx, key, value) => {
    setDraft(prev => {
      const next = { ...prev, generic: { ...prev.generic } };
      const list = [...(next.generic[day] || [])];
      list[idx] = { ...list[idx], [key]: value };
      next.generic[day] = list;
      return next;
    });
  };

  const removeGenericSlot = (day, idx) => {
    setDraft(prev => {
      const next = { ...prev, generic: { ...prev.generic } };
      const list = [...(next.generic[day] || [])];
      list.splice(idx, 1);
      next.generic[day] = list;
      return next;
    });
  };

  const getDayKeyFromDateString = (dateStr) => {
    if (!dateStr) return null;
    // Parse YYYY-MM-DD in local time to avoid timezone shifting
    const [y, m, d] = String(dateStr).split('-').map((v) => parseInt(v, 10));
    if (!y || !m || !d) return null;
    const date = new Date(y, (m - 1), d);
    if (Number.isNaN(date.getTime())) return null;
    const weekdayIdx = date.getDay(); // 0=Sun..6=Sat
    return dayOrder[weekdayIdx] || null;
  };

  const addSpecificDay = () => {
    if (!specificDate) return;
    setDraft(prev => {
      const exists = (prev.specific || []).some(s => s.date === specificDate);
      if (exists) return prev;
      const dayKey = getDayKeyFromDateString(specificDate);
      const genericSlots = (dayKey && prev.generic && prev.generic[dayKey]) ? [...prev.generic[dayKey]] : [];
      const initialSlots = (genericSlots && genericSlots.length > 0)
        ? genericSlots
        : [{ start: '09:00', end: '17:00' }];
      const next = { ...prev, specific: [...(prev.specific || []), { date: specificDate, slots: initialSlots, everyYear: false }] };
      return next;
    });
    setSpecificDate('');
  };

  const addSpecificSlot = (date) => {
    setDraft(prev => {
      const list = [...(prev.specific || [])];
      const idx = list.findIndex(s => s.date === date);
      if (idx === -1) return prev;
      const existingSlots = list[idx].slots || [];
      const taken = slotsToHourSet(existingSlots);
      // find first free 1-hour gap
      let startHour = null;
      for (let h = 0; h < 24; h++) {
        if (!taken.has(h)) { startHour = h; break; }
      }
      if (startHour === null) return prev; // no free hour
      const newSlots = [...existingSlots, { start: `${pad2(startHour)}:00`, end: `${pad2(Math.min(24, startHour + 1))}:00` }];
      const item = { ...list[idx], slots: newSlots };
      list[idx] = item;
      return { ...prev, specific: list };
    });
  };

  const updateSpecificSlot = (date, slotIdx, key, value) => {
    setDraft(prev => {
      const list = [...(prev.specific || [])];
      const idx = list.findIndex(s => s.date === date);
      if (idx === -1) return prev;
      const slots = [...(list[idx].slots || [])];
      slots[slotIdx] = { ...slots[slotIdx], [key]: value };
      list[idx] = { ...list[idx], slots };
      return { ...prev, specific: list };
    });
  };

  const updateSpecificEveryYear = (date, value) => {
    setDraft(prev => {
      const list = [...(prev.specific || [])];
      const idx = list.findIndex(s => s.date === date);
      if (idx === -1) return prev;
      list[idx] = { ...list[idx], everyYear: !!value };
      return { ...prev, specific: list };
    });
  };

  // Validation helpers for specific date time slots
  const parseTimeToFloat = (timeStr) => {
    if (!timeStr) return NaN;
    const [hh = '0', mm = '0'] = String(timeStr).split(':');
    const h = parseInt(hh, 10);
    const m = parseInt(mm, 10);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
    return h + m / 60;
  };
  const rangesOverlap = (aStart, aEnd, bStart, bEnd) => {
    // Treat ranges as half-open [start, end) so adjacent hours don't conflict
    return aStart < bEnd && aEnd > bStart;
  };
  const getSpecificSlotConflict = (date, slotIdx) => {
    const entry = (draft.specific || []).find(s => s.date === date);
    if (!entry) return null;
    const slots = entry.slots || [];
    const cur = slots[slotIdx];
    if (!cur) return null;
    const aStart = parseTimeToFloat(cur.start);
    const aEnd = parseTimeToFloat(cur.end);
    if (!Number.isFinite(aStart) || !Number.isFinite(aEnd)) return 'Invalid time.';
    if (aEnd <= aStart) return 'End time must be after start time.';
    for (let i = 0; i < slots.length; i++) {
      if (i === slotIdx) continue;
      const other = slots[i];
      const bStart = parseTimeToFloat(other.start);
      const bEnd = parseTimeToFloat(other.end);
      if (!Number.isFinite(bStart) || !Number.isFinite(bEnd)) continue;
      if (rangesOverlap(aStart, aEnd, bStart, bEnd)) {
        return 'This time overlaps an existing slot for the day.';
      }
    }
    return null;
  };

  const removeSpecificSlot = (date, slotIdx) => {
    setDraft(prev => {
      const list = [...(prev.specific || [])];
      const idx = list.findIndex(s => s.date === date);
      if (idx === -1) return prev;
      const slots = [...(list[idx].slots || [])];
      slots.splice(slotIdx, 1);
      list[idx] = { ...list[idx], slots };
      return { ...prev, specific: list };
    });
  };

  const removeSpecificDay = (date) => {
    setDraft(prev => {
      const list = (prev.specific || []).filter(s => s.date !== date);
      return { ...prev, specific: list };
    });
  };

  // Build a normalized payload for saving (no API call yet)
  const buildAvailabilityPayload = () => {
    // Normalize generic: collapse to canonical hour ranges per day (sorted, merged)
    const normalizedGeneric = {};
    dayOrder.forEach((d) => {
      const set = slotsToHourSet(draft.generic?.[d] || []);
      normalizedGeneric[d] = hourSetToSlots(set);
    });
    // Normalize specific: per date collapse and sort
    const normalizedSpecific = (draft.specific || []).map((entry) => {
      const set = slotsToHourSet(entry.slots || []);
      return { date: entry.date, everyYear: !!entry.everyYear, slots: hourSetToSlots(set) };
    }).sort((a, b) => String(a.date).localeCompare(String(b.date)));
    return {
      generic: normalizedGeneric,
      specific: normalizedSpecific
    };
  };

  const isValidSlug = (value) => {
    if (!value) return true;
    return /^[a-z0-9-]{3,64}$/.test(value);
  };

  const getPublicUrl = () => {
    if (!publicSlug) return '';
    return `${resolvePublicOrigin()}/calendar/${publicSlug}`;
  };

  const handlePrintQr = () => {
    try {
      const url = getPublicUrl();
      if (!url) return;
      // Generate inline SVG markup (embedded directly in print HTML to avoid broken images)
      const svgMarkup = ReactDOMServer.renderToStaticMarkup(
        <QRCodeSVG value={url} size={180} level="M" fgColor="#111111" bgColor="#ffffff" />
      );
      const resolvedCompany = companyDetails || company || {};
      const companyName = resolvedCompany?.name || resolvedCompany?.legalName || 'Company';
      const who = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
      const userEmail = (user?.email || '').toLowerCase();
      const companyPhone = resolvedCompany?.phone || '';
      const companyWebsite = (resolvedCompany?.website || '').toLowerCase();
      const companyAddress = (() => {
        const addr = resolvedCompany?.address;
        if (!addr) return '';
        if (typeof addr === 'string') return addr;
        if (typeof addr === 'object') return addr.address || '';
        return '';
      })();
      const logoUrl = resolvedCompany?.logo || '';
      const html = `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>Calendar QR</title>
            <style>
              @page { margin: 6mm; }
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji';
                color: #111827;
                margin: 0;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .card {
                width: 90mm;
                min-height: 54mm;
                border: 1px solid #d1d5db;
                border-radius: 0px;
                padding: 6mm;
                margin: 0 auto;
                box-sizing: border-box;
              }
              .row {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 6mm;
                align-items: center;
              }
              .brand {
                display: flex;
                align-items: center;
                gap: 3mm;
                margin-bottom: 2mm;
              }
              .brand img.logo {
                width: 10mm; height: 10mm; object-fit: cover; border-radius: 2mm; border: 1px solid #e5e7eb;
              }
              .brand .title {
                font-size: 10px; font-weight: 800; line-height: 1.1;
              }
              .company-details {
                display: flex; flex-direction: column; gap: 0.8mm; font-size: 6px; color: #374151;
              }
              .who {
                font-size: 10px; color: #374151; margin-top: 1mm;
              }
              .qr-box {
                display: flex; flex-direction: column; align-items: center; gap: 5px;
                border: 1px solid #e5e7eb; border-radius: 8px; padding: 4mm;
                background: #ffffff;
              }
              .qr-box .qr svg { width: 26mm; height: 26mm; display:block; }
              .scan { margin-top: 1mm; color:#374151; font-size: 9px; text-align:center; }
              .url { color:#6b7280; font-size: 8px; word-break: break-word; text-align:center; }
            </style>
          </head>
          <body>
            <div class="card">
              <div class="row">
                <div>
                  <div class="brand">
                    ${logoUrl ? `<img class="logo" src="${logoUrl}" alt="Logo" />` : ``}
                   
                  </div>
                   <div class="title">${companyName}</div>
                  <div class="company-details">
                    ${companyAddress ? `<div>${companyAddress}</div>` : ``}
                    ${companyPhone ? `<div>${companyPhone}</div>` : ``}
                    ${userEmail ? `<div>${userEmail}</div>` : ``}
                    ${companyWebsite ? `<div>${companyWebsite}</div>` : ``}
                  </div>
                  ${who ? `<div class="who">${who}</div>` : ``}
                </div>
                <div class="qr-box">
                  <div class="qr">${svgMarkup}</div>
                  <div class="scan">Scan to book a meeting</div>
                </div>
              </div>
            </div>
            <script>
              window.addEventListener('load', function () {
                setTimeout(function () {
                  try { window.focus(); window.print(); } catch (e) {}
                }, 50);
              });
            </script>
          </body>
        </html>
      `;
      const blob = new Blob([html], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      const w = window.open(blobUrl, '_blank', 'noopener');
      if (!w) return;
      setTimeout(() => {
        try { URL.revokeObjectURL(blobUrl); } catch (_) {}
      }, 10000);
    } catch (_) {
      // ignore
    }
  };

  const handleSave = () => {
    const payload = buildAvailabilityPayload();
    const timezone = (() => {
      try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; }
    })();
    if (user?.id) {
      (async () => {
        setIsSavingAvailability(true);
        try {
          // Prevent save if slug invalid
          if (!isValidSlug(publicSlug)) {
            setSlugError('Use 3-64 chars: a-z, 0-9, -');
            setIsSavingAvailability(false);
            return;
          }
          // Prevent save if slug is known taken
          if (publicSlug && isSlugAvailable === false) {
            setSlugError('This slug is already in use. Please choose another.');
            setIsSavingAvailability(false);
            return;
          }
          const { data } = await api.put(`/api/users/${user.id}/availability`, { schedule: payload, timezone, publicSlug });
          if (data?.is_success && data.availability?.schedule) {
            onSave && onSave(data.availability.schedule);
            setToast('Availability saved');
            return;
          }
        } catch (e) {
          // ignore errors for now; still propagate local payload to caller
        } finally {
          setIsSavingAvailability(false);
        }
        onSave && onSave(payload);
        setToast('Availability saved');
      })();
    } else {
      onSave && onSave(payload);
      setToast('Availability saved');
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose && onClose();
  };

  // Formatting helpers (12-hour AM/PM)
  const formatHourLabel = (h) => {
    const hNorm = ((Number(h) || 0) % 24 + 24) % 24; // normalize into [0,24)
    const period = hNorm < 12 ? 'AM' : 'PM';
    const hour12 = hNorm % 12 === 0 ? 12 : hNorm % 12;
    return `${pad2(hour12)}:00 ${period}`;
  };
  const formatRangeLabel = (startH, endH) => {
    return `${formatHourLabel(startH)} - ${formatHourLabel(Math.min(24, endH))}`;
  };
  const formatSlotLabel = (slot) => {
    const sh = parseInt(String(slot.start || '00:00').split(':')[0], 10);
    const eh = parseInt(String(slot.end || '00:00').split(':')[0], 10);
    if (!Number.isFinite(sh) || !Number.isFinite(eh)) return `${slot.start} - ${slot.end}`;
    return formatRangeLabel(sh, eh);
  };
  // SVG clock helpers for preview (dual-ring: AM inner, PM outer)
  const polarToCartesian = (cx, cy, r, angleDeg) => {
    const rad = (angleDeg - 90) * Math.PI / 180.0;
    return {
      x: cx + r * Math.cos(rad),
      y: cy + r * Math.sin(rad)
    };
  };
  const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, r, startAngle);
    const end = polarToCartesian(cx, cy, r, endAngle);
    const sweep = 1; // clockwise
    const delta = Math.max(0, endAngle - startAngle);
    const largeArcFlag = delta >= 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} ${sweep} ${end.x} ${end.y}`;
  };
  const renderDualClockArcs = (slots, size = 55) => {
    const cx = size / 2;
    const cy = size / 2;
    const rOuter = size / 2 - 6;   // PM
    const rInner = rOuter - 8;     // AM
    const colorAm = '#10b981'; // green
    const colorPm = '#3b82f6'; // blue
    const trackAm = 'rgba(16,185,129,0.25)';
    const trackPm = 'rgba(59,130,246,0.25)';
    // Convert string time to numeric hour (e.g., "13:30" -> 13.5)
    const toHour = (hStr) => {
      const [hh = '0', mm = '0'] = String(hStr || '0:0').split(':');
      const h = parseInt(hh, 10) || 0;
      const m = parseInt(mm, 10) || 0;
      return Math.max(0, Math.min(24, h + m / 60));
    };
    const arcs = [];
    (slots || []).forEach((s, idx) => {
      const start = toHour(s.start);
      const end = Math.max(start, toHour(s.end));
      // AM intersection [0,12]
      const amStart = Math.max(0, Math.min(12, start));
      const amEnd = Math.max(0, Math.min(12, end));
      if (amEnd > amStart) {
        const a0 = (amStart / 12) * 360;
        const a1 = (amEnd / 12) * 360;
        const d = describeArc(cx, cy, rInner, a0, a1);
        arcs.push(<path key={`arc-am-${idx}`} d={d} stroke={colorAm} strokeWidth="5" fill="none" strokeLinecap="round" />);
      }
      // PM intersection [12,24]
      const pmStart = Math.max(12, Math.min(24, start));
      const pmEnd = Math.max(12, Math.min(24, end));
      if (pmEnd > pmStart) {
        const p0 = ((pmStart - 12) / 12) * 360;
        const p1 = ((pmEnd - 12) / 12) * 360;
        const d = describeArc(cx, cy, rOuter, p0, p1);
        arcs.push(<path key={`arc-pm-${idx}`} d={d} stroke={colorPm} strokeWidth="5" fill="none" strokeLinecap="round" />);
      }
    });
    // Base tracks for both rings
    arcs.unshift(
      <circle key="track-outer" cx={cx} cy={cy} r={rOuter} stroke={trackPm} strokeWidth="3" fill="none" />,
      <circle key="track-inner" cx={cx} cy={cy} r={rInner} stroke={trackAm} strokeWidth="3" fill="none" />
    );
    return arcs;
  };

  return (
    <div className="modal-overlay task-modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content task-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header task-modal-header">
          <div className="task-modal-header-content">
            <div className="task-title-row">
              <span className="task-id-pill">Availability</span>
            </div>
          </div>
          <button className="close-button" onClick={onClose} aria-label="Close" title="Close">Close</button>
        </div>

        <div className="modal-body task-modal-body">
              {!!toast && (
                <div style={{ marginBottom:8, color:'#065f46', background:'#ecfdf5', padding:'8px 12px', borderRadius:8 }}>{toast}</div>
              )}
              <div
                className="availability-explainer"
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: '10px 12px',
                  color: '#475569',
                  fontSize: 14,
                  lineHeight: '20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 10
                }}
              >
                <div>
                  Set up your personal calendar availability. <br/>Set when you’re available to meet. Use weekly hours for a repeating schedule, <br /> or add specific dates for exceptions and special days.
                </div>
                <div>
                  {isCheckingCalendar ? (
                    <span className="integration-badge small" style={{ color:'#64748b' }}>Checking calendar…</span>
                  ) : isCalendarConnected ? (
                    <span className="integration-badge small" style={{ display:'inline-flex', alignItems:'center', gap:6, color:'#16a34a' }} title="Calendar connected">
                      <FaCheckCircle aria-hidden="true" />
                      <strong>Connected with Google Calendar</strong>
                      {calendarAccount ? <span style={{ color:'#64748b' }}>({calendarAccount})</span> : null}
                    </span>
                  ) : (
                    <button
                      className={`btn-secondary small ${isCalendarConnected ? 'active' : ''}`}
                      onClick={() => {
                        const openAuthorize = (authorizeUrl) => {
                          const popup = window.open(
                            authorizeUrl,
                            'googleCalendarOAuth',
                            'width=520,height=640,noopener,noreferrer'
                          );
                          if (!popup) {
                            window.location.href = authorizeUrl;
                            return;
                          }
                          const timer = window.setInterval(() => {
                            if (popup.closed) {
                              window.clearInterval(timer);
                              refreshCalendarConnectionStatus();
                            }
                          }, 500);
                        };

                        if (googleAuthorizeUrl) {
                          openAuthorize(googleAuthorizeUrl);
                          return;
                        }

                        api.get('/api/integrations/google/authorize-link', {
                          params: { returnOrigin: window.location.origin }
                        }).then(({ data }) => {
                          if (data?.is_success && data.authorizeUrl) {
                            setGoogleAuthorizeUrl(data.authorizeUrl);
                            window.location.href = data.authorizeUrl;
                          } else {
                            setToast('Could not start Google Calendar connection. Please try again.');
                          }
                        }).catch(() => {
                          setToast('Could not start Google Calendar connection. Please try again.');
                        });
                      }}
                      disabled={isCheckingCalendar}
                      aria-label="Google Calendar"
                      title={isCalendarConnected ? 'Connected to Google Calendar' : 'Connect your Google Calendar'}
                      style={{
                        background: isCalendarConnected ? '#0f172a' : '#ffffff',
                        color: isCalendarConnected ? '#ffffff' : '#0f172a',
                        border: '1px solid #0f172a',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      {isCheckingCalendar ? 'Checking…' : (isCalendarConnected ? 'My Google Calendar: Connected' : 'My Google Calendar: Connect')}
                    </button>
                  )}
                </div>
              </div>
          <div className="form-row">
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Availability Mode</label>
              <div className="availability-mode-row">
                <div className="view-toggle" role="tablist" aria-label="Availability mode" style={{ marginTop: 4, justifyContent:'flex-start' }}>
                <button
                  className={`btn-secondary small ${mode === 'generic' ? 'active' : ''}`}
                  role="tab"
                  aria-selected={mode === 'generic'}
                  onClick={() => setMode('generic')}
                >
                  Generic (weekly)
                </button>
                <button
                  className={`btn-secondary small ${mode === 'specific' ? 'active' : ''}`}
                  role="tab"
                  aria-selected={mode === 'specific'}
                  onClick={() => setMode('specific')}
                >
                  Specific dates
                </button>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'nowrap', whiteSpace:'nowrap' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ color:'#64748b', fontSize:12 }}>shareable link:</span>
                    <span style={{ color:'#6b7280', fontSize:12 }}>calendar/</span>
                    <div style={{ display:'flex', flexDirection:'column' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'nowrap' }}>
                        <input
                          type="text"
                          className="form-control"
                          value={publicSlug}
                          onChange={(e) => {
                            const v = String(e.target.value || '').trim();
                            const clean = v.toLowerCase();
                            setPublicSlug(clean);
                            setSlugError(isValidSlug(clean) ? '' : 'Use 3-64 chars: a-z, 0-9, -');
                          }}
                          placeholder="your-slug"
                          style={{
                            maxWidth: 220,
                            padding:'6px 8px',
                            height:28,
                            fontFamily:'monospace',
                            borderColor: slugError
                              ? '#ef4444'
                              : (publicSlug
                                  ? (isSlugAvailable === true ? '#10b981' : (isSlugAvailable === false ? '#ef4444' : undefined))
                                  : undefined),
                            background: (publicSlug && !slugError && isSlugAvailable === true) ? '#ecfdf5' : undefined
                          }}
                        />
                        {publicSlug ? (
                          <span
                            style={{
                              fontSize:12,
                              color: slugError || isSlugAvailable === false ? '#b91c1c' : (isSlugAvailable === true ? '#16a34a' : '#64748b')
                            }}
                          >
                            {isCheckingSlug ? 'Checking…' : (slugError ? 'Invalid' : (isSlugAvailable === true ? 'Available' : (isSlugAvailable === false ? 'Taken' : '')))}
                          </span>
                        ) : null}
                        {!!publicSlug && !slugError && (
                          <>
                            <span
                              role="button"
                              tabIndex={0}
                              title="Preview public page"
                              aria-label="Preview public page"
                              onClick={() => {
                                const url = getPublicUrl();
                                if (url) window.open(url, '_blank', 'noopener,noreferrer');
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); const url = getPublicUrl(); if (url) window.open(url, '_blank', 'noopener,noreferrer'); } }}
                              style={{ display:'inline-flex', alignItems:'center', color:'#111827', cursor:'pointer' }}
                            >
                              <FiEye style={{ fontSize:16 }} />
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              title="Copy public link"
                              aria-label="Copy public link"
                              onClick={() => {
                                const url = getPublicUrl();
                                if (!url) return;
                                try { navigator.clipboard.writeText(url); } catch (_) {}
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); const url = getPublicUrl(); if (!url) return; try { navigator.clipboard.writeText(url); } catch (_) {} } }}
                              style={{ display:'inline-flex', alignItems:'center', color:'#111827', marginLeft:6, cursor:'pointer' }}
                            >
                              <FiClipboard style={{ fontSize:16 }} />
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              title="Show QR code"
                              aria-label="Show QR code"
                              onClick={() => setQrOpen(true)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setQrOpen(true); } }}
                              style={{ display:'inline-flex', alignItems:'center', color:'#111827', marginLeft:6, cursor:'pointer' }}
                            >
                              <FaQrcode style={{ fontSize:16 }} />
                            </span>
                          </>
                        )}
                      </div>
                      {!!slugError && (
                        <div className="field-error-message" style={{ marginTop: 4 }}>{slugError}</div>
                      )}
                    </div>
                  </div>
                </div>
              {/* {!isCheckingCalendar && isCalendarConnected ? (
                  <span className="integration-badge google-meet small" style={{ display:'inline-flex', alignItems:'center', gap:6, color:'#16a34a' }}>
                    <FaCheckCircle aria-hidden="true" style={{ fontSize: 16 }} />
                    <strong>Connected with Google Calendar</strong>
                  </span>
                ) : null} */}
              </div>
            </div>
          </div>

          {mode === 'generic' ? (
            <>
              {/* Days left as buttons; hours as multi-select buttons on right */}
              <div className="form-row">
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'180px 1fr 180px', gap: 10, alignItems:'start' }}>
                    <div>
                      <label>Days</label>
                      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                        {dayOrder.map((d) => {
                          const active = selectedDay === d || getDayHourSet(d).size > 0;
                          return (
                            <button
                              key={d}
                              className={`btn-secondary small availability-day-button ${active ? 'active' : ''} ${selectedDay === d ? 'pulse' : ''}`}
                              onClick={() => handleToggleDay(d)}
                              aria-pressed={active}
                              style={{ justifyContent:'flex-start' }}
                            >
                              {dayLabel[d]}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <div className="availability-actions">
                        <label>Hours</label>
                        <div className="availability-actions-right">
                          <button
                            className="btn-secondary "
                            title="Apply current selection to all days"
                            onClick={handleApplyCurrentToAll}
                            disabled={mode !== 'generic' || !selectedDay}
                          >
                            {/* icon removed; text only to match cancel style */}
                            Apply to all
                          </button>
                          <span
                            className="clear-all-span"
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              setDraft(prev => {
                                const next = { ...prev, generic: { ...prev.generic } };
                                dayOrder.forEach(d => { next.generic[d] = []; });
                                return next;
                              });
                              setSelectedDay(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setDraft(prev => {
                                  const next = { ...prev, generic: { ...prev.generic } };
                                  dayOrder.forEach(d => { next.generic[d] = []; });
                                  return next;
                                });
                                setSelectedDay(null);
                              }
                            }}
                            title="Clear all"
                            aria-label="Clear all"
                            style={{ display:'inline-flex', alignItems:'center', color:'#111827', cursor:'pointer' }}
                          >
                            <FiTrash2 style={{ fontSize:16 }} />
                          </span>
                        </div>
                      </div>
                      <div className="detail-rows">
                        <div className="detail-row">
                          <div className="detail-item full-width" style={{ paddingTop: 8 }}>
                            <div className="availability-scroll">
                              {!selectedDay ? (
                                <div className="hours-empty-state">Select a day to choose hours</div>
                              ) : (
                                <>
                                  {/* AM */}
                                  <div style={{ marginBottom: 6 }}>
                                    <span className="detail-label" style={{ textTransform:'none' }}>AM</span>
                                  </div>
                                  <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
                                    {Array.from({ length: 12 }).map((_, idx) => {
                                      const h = idx; // 0..11
                                      const selected = isHourSelectedForSelectedDay(h);
                                      return (
                                        <button
                                          key={`h-am-${h}`}
                                          type="button"
                                          className={`btn-secondary small ${selected ? 'active' : ''}`}
                                          aria-pressed={selected}
                                          onClick={() => toggleHour(h)}
                                          style={{ minWidth: 88, justifyContent:'center',background:selected ? '#10b981' : '#ffffff', color: selected ? '#ffffff' : '#0f172a',borderColor:selected ? '#10b981' : '#e2e8f0' }}
                                        >
                                          {formatRangeLabel(h, h + 1)}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {/* PM */}
                                  <div style={{ marginBottom: 6 }}>
                                    <span className="detail-label" style={{ textTransform:'none' }}>PM</span>
                                  </div>
                                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                                    {Array.from({ length: 12 }).map((_, idx) => {
                                      const h = 12 + idx; // 12..23
                                      const selected = isHourSelectedForSelectedDay(h);
                                      return (
                                        <button
                                          key={`h-pm-${h}`}
                                          type="button"
                                          className={`btn-secondary small ${selected ? 'active' : ''}`}
                                          aria-pressed={selected}
                                          onClick={() => toggleHour(h)}
                                          style={{ minWidth: 88, justifyContent:'center',background:selected ? '#3b82f6' : '#ffffff', color: selected ? '#ffffff' : '#0f172a',borderColor:selected ? '#3b82f6' : '#e2e8f0' }}
                                        >
                                          {formatRangeLabel(h, h + 1)}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="preview-header">
                        <label>Preview</label>
                        <div className="preview-legend">
                          <span className="legend-item">
                            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
                              <circle cx="7" cy="7" r="5" stroke="#10b981" strokeWidth="3" fill="none" />
                            </svg>
                            AM
                          </span>
                          <span className="legend-item">
                            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
                              <circle cx="7" cy="7" r="5" stroke="#3b82f6" strokeWidth="3" fill="none" />
                            </svg>
                            PM
                          </span>
                        </div>
                      </div>
                      <div className="detail-rows">
                        <div className="detail-row">
                          <div className="detail-item full-width" style={{ paddingTop: 8 }}>
                            <div className="availability-scroll" style={{ display:'grid', gap:5 }}>
                              {dayOrder.map((d) => {
                                const set = getDayHourSet(d);
                                const slots = hourSetToSlots(set);
                                return (
                                  <div key={`pv-${d}`} style={{ display:'grid', gridTemplateColumns:'70px 1fr', alignItems:'center', columnGap: 5 }}>
                                    <div className="availability-clock">
                                      <svg width="55" height="55" viewBox="0 0 55 55">
                                        {renderDualClockArcs(slots, 55)}
                                      </svg>
                                    </div>
                                    <div style={{ display:'flex', gap:5, flexWrap:'wrap', alignItems:'center' }}>
                                      <span style={{ minWidth: 28, color:'#64748b',fontSize:14 }}>{dayLabel[d]}</span>
                                     {/*  {slots.length ? (
                                        slots.map((s, idx) => (
                                          <span key={`pill-${d}-${idx}`} className="availability-pill" style={{ fontSize:10 }}>
                                            {formatSlotLabel(s)}
                                          </span>
                                        ))
                                      ) : (
                                        <span className="detail-value" style={{ color:'#94a3b8' }}>No hours</span>
                                      )} */}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="form-row">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Specific Dates</label>
                <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8, flexWrap:'nowrap' }}>
                  <input
                    type="date"
                    className="form-control"
                    value={specificDate}
                    onChange={(e) => setSpecificDate(e.target.value)}
                    style={{ maxWidth: 220 }}
                  />
                  <span className="icon-span" role="button" tabIndex={0}
                        onClick={addSpecificDay}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addSpecificDay(); } }}
                        title="Add date" aria-label="Add date">+</span>
                </div>
                <div className="detail-rows">
                  {(draft.specific || []).length === 0 && (
                    <div className="detail-value" style={{ color:'#64748b' }}>No specific dates added</div>
                  )}
                  {(draft.specific || []).map(item => (
                    <div className="detail-row" key={item.date}>
                      <div className="detail-item full-width">
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 6, flexWrap:'nowrap' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:5, flexWrap:'nowrap' }}>
                            <span className="detail-label" style={{ textTransform: 'none', marginRight: 10 }}>{item.date}</span>
                            <label style={{ display:'inline-flex', alignItems:'center', gap:5, color:'#64748b', fontSize:12, cursor:'pointer', whiteSpace:'nowrap',marginTop:5 }}>
                              <input
                                type="checkbox"
                                checked={!!item.everyYear}
                                onChange={(e) => updateSpecificEveryYear(item.date, e.target.checked)}
                              />
                              Every year
                            </label>
                          </div>
                          <div style={{ display:'flex', gap:8, flexWrap:'nowrap' }}>
                            <span className="icon-span small" role="button" tabIndex={0}
                                  onClick={() => addSpecificSlot(item.date)}
                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addSpecificSlot(item.date); } }}
                                  title="Add slot" aria-label="Add slot">+</span>
                            <span className="icon-span red small" role="button" tabIndex={0}
                                  onClick={() => removeSpecificDay(item.date)}
                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); removeSpecificDay(item.date); } }}
                                  title="Remove date" aria-label="Remove date">-</span>
                          </div>
                        </div>
                        <div className="specific-slots-grid">
                          {(item.slots || []).map((slot, idx) => (
                            <div key={`${item.date}-${idx}`} className="specific-slot-row">
                              <div className="specific-slot-inputs">
                                <input
                                  type="time"
                                  className={`form-control ${getSpecificSlotConflict(item.date, idx) ? 'error' : ''}`}
                                  value={slot.start}
                                  onChange={(e) => updateSpecificSlot(item.date, idx, 'start', e.target.value)}
                                  aria-invalid={!!getSpecificSlotConflict(item.date, idx)}
                                  style={{ maxWidth: 140 }}
                                />
                                <span style={{ color:'#64748b' }}>to</span>
                                <input
                                  type="time"
                                  className={`form-control ${getSpecificSlotConflict(item.date, idx) ? 'error' : ''}`}
                                  value={slot.end}
                                  onChange={(e) => updateSpecificSlot(item.date, idx, 'end', e.target.value)}
                                  aria-invalid={!!getSpecificSlotConflict(item.date, idx)}
                                  style={{ maxWidth: 140 }}
                                />
                                <span className="icon-span red" role="button" tabIndex={0}
                                      onClick={() => removeSpecificSlot(item.date, idx)}
                                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); removeSpecificSlot(item.date, idx); } }}
                                      title="Remove slot" aria-label="Remove slot">-</span>
                              </div>
                              {getSpecificSlotConflict(item.date, idx) && (
                                <div className="field-error-message">
                                  {getSpecificSlotConflict(item.date, idx)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        {(!item.slots || item.slots.length === 0) && (
                          <div className="detail-value" style={{ color:'#64748b' }}>No slots</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer task-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="submit-button" onClick={handleSave} disabled={isSavingAvailability} data-loading={isSavingAvailability}>
            {isSavingAvailability ? (<><span className="button-loading-spinner" /> Saving…</>) : 'Save'}
          </button>
        </div>
      </div>
      {/* QR Modal */}
      {qrOpen && (
        <div
          className="modal-overlay"
          style={{
            position:'fixed',
            inset:0,
            background:'rgba(0,0,0,0.4)',
            display:'flex',
            alignItems:'center',
            justifyContent:'center',
            zIndex: 2000
          }}
          onClick={() => setQrOpen(false)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background:'#fff',
              borderRadius:12,
              width:'min(460px, 92vw)',
              padding:16,
              boxShadow:'0 10px 30px rgba(0,0,0,0.15)'
            }}
          >
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
              <div style={{ display:'flex', flexDirection:'column' }}>
                <div style={{ fontWeight:700, fontSize:16 }}>Share your booking link</div>
                <div style={{ color:'#6b7280', fontSize:12, marginTop:2 }}>
                  Share this QR with clients so they can book a meeting on your <b>personal calendar</b>.
                </div>
              </div>
             
            </div>
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:10 }}>
              <div style={{ border:'1px solid #e5e7eb', borderRadius:12, padding:16, background:'#fff' }}>
                <QRCodeSVG
                  value={getPublicUrl() || ''}
                  size={240}
                  imageSettings={{ src: (company?.logo || '/logo.png'), height: 40, width: 40, excavate: true }}
                  level="M"
                  fgColor="#111111"
                  bgColor="#ffffff"
                />
              </div>
              <div style={{ fontSize:12, color:'#6b7280' }}>
                {getPublicUrl()}
              </div>
              <div style={{ display:'flex', gap:8 }}>
              <button
                onClick={() => setQrOpen(false)}
                className="btn btn-secondary"
              >
                Close
              </button>
                <button className="btn btn-secondary" onClick={handlePrintQr}>Print</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarAvailabilityModal;


